import { AxiosError } from 'axios';
import classNames from 'classnames';
import { isEqual } from 'lodash';
import React, {
  useState, useEffect,
  useCallback, useMemo,
} from 'react';
import usePlacesService from 'react-google-autocomplete/lib/usePlacesAutocompleteService';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Row, Col,
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  Input, Label,
  FormGroup, ListGroup, ListGroupItem,
  FormFeedback,
} from 'reactstrap';

import {
  markAsUrgentDispatch,
} from '@reducers/referral/ReferralAction';
import { handleError } from '@services/ErrorHandler';
import LocaleService from '@services/LocaleService';
import { CustomButton } from '@shared/buttons';
import Checkbox from '@shared/components/input/Checkbox';
import InputField from '@shared/components/input/InputField';
import useFormPrompt from '@shared/hooks/useFormPrompt';
import { SavedIcon, InfoFilledIcon } from '@shared/icons';

import { BasicInfoSkeleton, ContactInfoSkeleton, AddressInfoSkeleton } from './skeleton-loading/PatientInfoSkeleton';
import { useGuestReferral, useReferralStepRequests } from '../guest/GuestReferralContext';

import '../SendReferralOrderPageStyle.scss';

type PatientInformationFormProps = {
  open: string;
  toggle: (e: any) => void;
  formType: string;
  loadingDetails: boolean;
  // Runs after every successful save with the saved referral; resolving false
  // keeps the form from moving on to the next step.
  afterSave?: (latestReferral: any, isNext: boolean) => Promise<boolean>;
};

const PatientInformationForm = ({
  open, toggle, formType, loadingDetails, afterSave,
}: PatientInformationFormProps) => {
  const dispatch = useDispatch<any>();
  const location = useLocation();
  const navigate = useNavigate();
  const requests = useReferralStepRequests();
  const guest = useGuestReferral();
  const isUpdate = location?.pathname?.includes('view');
  const returnTo = location?.state?.returnTo;
  const i18n = LocaleService.getTranslations('referrals.sendReferral');
  const referralDetails = useSelector(({ Referral }) => Referral.referralData);
  const isUrgent = useSelector(({ Referral }) => Referral.isUrgent);
  const isCompleted = referralDetails?.status === 'completed';
  const [ isFocused, setIsFocused ] = useState(false);
  const [ saved, setSaved ] = useState(false);
  const stepId = formType === 'referral' ? '2' : '1';
  const nextStepId = formType === 'referral' ? '3' : '2';
  const [ formData, setFormData ] = useState<any>({
    first_name: '',
    last_name: '',
    birthdate: '',
    email: '',
    mobile_number: '+1',
    street: '',
    city: '',
    province: '',
    postal_code: '',
    gps_location: '',
    type: '',
    referral_id: '',
    is_urgent: isUrgent || referralDetails?.is_urgent || false,
    should_notify: referralDetails?.patient?.should_notify ?? false,
  });
  const [ errors, setErrors ] = useState<any>([]);
  const [ loading, setLoading ] = useState(false);
  const [ isSaving, setIsSaving ] = useState(false);
  const {
    placePredictions,
    placesService,
    getPlacePredictions,
  } = usePlacesService({
    apiKey: process.env.REACT_APP_GOOGLE_PLACES_API_KEY,
  });
  const initialData = useMemo(() => {
    if (open !== stepId) return {}; // Return empty object if accordion is closed

    return {
      first_name: referralDetails?.patient?.first_name ?? '',
      last_name: referralDetails?.patient?.last_name ?? '',
      birthdate: referralDetails?.patient?.birthdate ?? '',
      email: referralDetails?.patient?.email ?? '',
      mobile_number: referralDetails?.patient?.mobile_number?.replace('+1', '') ?? '',
      street: referralDetails?.patient?.address?.street ?? '',
      city: referralDetails?.patient?.address?.city ?? '',
      province: referralDetails?.patient?.address?.province ?? '',
      postal_code: referralDetails?.patient?.address?.postal_code ?? '',
      gps_location: referralDetails?.patient?.address?.gps_location ?? '',
      type: formType ?? '',
      referral_id: referralDetails?.id ?? '',
      is_urgent: referralDetails?.is_urgent || false,
      should_notify: referralDetails?.patient?.should_notify ?? false,
    };
  }, [ open, referralDetails, formType, stepId ]);
  const isDirty = useCallback(() => {
    if (open !== stepId) return false; // Skip the check if accordion is closed
    return !isEqual(formData, initialData);
  }, [ initialData, formData, open, stepId ]);

  const handleOnFocus = () => setIsFocused(true);
  const handleOnBlur = () => {
    // Delay closing the list group to allow clicking on an item
    setTimeout(() => setIsFocused(false), 200);
  };

  const onSelectAddress = async (data: any) => {
    try {
      await placesService.getDetails({ placeId: data.place_id }, (placeDetails: any) => {
        if (!placeDetails) return;
        const { geometry, address_components: addressComponents } = placeDetails;
        const newFormData: any = {
          street: '',
          city: '',
          province: '',
          postal_code: '',
          gps_location: '',
        };
        addressComponents.forEach((component: any) => {
          const { types, long_name: longName } = component;

          if (types.includes('street_number')) {
            newFormData.street = `${longName} `;
          }
          if (types.includes('route')) {
            newFormData.street += longName;
          }
          if (types.includes('locality')) {
            newFormData.city = longName;
          }
          if (types.includes('administrative_area_level_1')) {
            newFormData.province = longName;
          }
          if (types.includes('postal_code')) {
            newFormData.postal_code = longName;
          }
        });

        if (geometry && geometry.location) {
          // Extract latitude and longitude
          const { lat, lng } = geometry.location;
          newFormData.gps_location = `${lat()}, ${lng()}`;
        }

        setFormData((prevState: any) => ({
          ...prevState,
          ...newFormData,
        }));
      });
    } catch (error) {
      handleError(error);
    }
  };

  const handleInputChange = (label: string, value: any) => {
    setFormData((prev: any) => ({ ...prev, [label]: value }));

    setErrors((prev: any) => ({ ...prev, [label]: '' }));
  };

  const handleShouldNotify = () => {
    setFormData((prev: any) => ({
      ...prev,
      should_notify: !prev.should_notify,
    }));
  };

  const savePatientInformation = async (shouldGoNext: boolean) => {
    setLoading(true);
    setErrors([]);
    const newData = {
      ...formData,
      is_urgent: isUrgent || referralDetails?.is_urgent || false,
    };
    try {
      const request = referralDetails?.patient?.id
        ? requests.updatePatientInformationRequest(newData, referralDetails.patient.id)
        : requests.createPatientInformationRequest(newData);

      const result = await dispatch(request).$promise;
      setSaved(true);
      if (afterSave) {
        // Phase 3 (backend): answers {data: <ReferralResource>}.
        const canContinue = await afterSave(result.data?.data, shouldGoNext);
        if (!canContinue) return;
      }
      if (shouldGoNext) toggle(nextStepId);
    } catch (error) {
      const { response: errorResponse } = error as AxiosError;

      if (errorResponse) {
        if (errorResponse.status === 422) {
          const { errors: errorData } = errorResponse.data;
          setErrors(errorData);
          setLoading(false);
          return;
        }
        handleError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const onHandleNext = async () => {
    await savePatientInformation(true);
  };

  const onSaveExit = async (e?: any) => {
    e?.stopPropagation();
    if (!isDirty()) {
      navigate(returnTo);
      return;
    }
    setIsSaving(true);
    setErrors([]);
    const newData = {
      ...formData,
      is_urgent: isUrgent || referralDetails?.is_urgent || false,
    };
    try {
      await dispatch(requests.updatePatientInformationRequest(newData,
        referralDetails.patient.id)).$promise;
      setSaved(true);
      navigate(returnTo);
    } catch (error) {
      const { response: errorResponse } = error as AxiosError;

      if (errorResponse) {
        if (errorResponse.status === 422) {
          const { errors: errorData } = errorResponse.data;
          setErrors(errorData);
          setIsSaving(false);
          return;
        }
        handleError(error);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const onDiscardChanges = () => {
    dispatch(markAsUrgentDispatch(referralDetails?.is_urgent || false));

    setFormData((prevState: any) => ({
      ...prevState,
      first_name: referralDetails?.patient?.first_name ?? '',
      last_name: referralDetails?.patient?.last_name ?? '',
      birthdate: referralDetails?.patient?.birthdate ?? '',
      email: referralDetails?.patient?.email ?? '',
      mobile_number: referralDetails?.patient?.mobile_number?.replace('+1', '') ?? '',
      street: referralDetails?.patient?.address?.street ?? '',
      city: referralDetails?.patient?.address?.city ?? '',
      province: referralDetails?.patient?.address?.province ?? '',
      postal_code: referralDetails?.patient?.address?.postal_code ?? '',
      gps_location: referralDetails?.patient?.address?.gps_location ?? '',
      type: formType ?? '',
      referral_id: referralDetails?.id ?? '',
      is_urgent: referralDetails?.is_urgent || false,
      should_notify: referralDetails?.patient?.should_notify ?? false,
    }));
  };

  const { confirm } = useFormPrompt({
    onSave: async () => {
      // Save form logic
      await savePatientInformation(false);
    },
    onDiscard: () => {
      // Discard changes logic
      onDiscardChanges();
    },
    isFormDirty: isDirty(),
  });

  const toggleAccordion = async () => {
    // Call confirm prompt before closing accordion
    // if there are any changes
    if (open === stepId && isDirty()) {
      const result = await confirm();
      if (!result) return;
    }
    toggle(stepId);
  };

  const onHandleBack = async () => {
    if (isDirty()) {
      const result = await confirm();
      if (!result) return;
    }
    // A guest (ENH-052) has no page to return to: Back is the previous step.
    if (guest) {
      toggle(String(Number(stepId) - 1));
      return;
    }
    navigate(returnTo);
  };

  useEffect(() => {
    setFormData((prevState: any) => ({
      ...prevState,
      type: formType,
      is_urgent: isUrgent,
    }));
    setSaved(false);
  }, [ formType, isUrgent ]);

  useEffect(() => {
    const initiate = () => {
      if (open !== stepId) return;
      setFormData((prevState: any) => ({
        ...prevState,
        first_name: referralDetails?.patient?.first_name || formData?.first_name,
        last_name: referralDetails?.patient?.last_name || formData?.last_name,
        birthdate: referralDetails?.patient?.birthdate || formData?.birthdate,
        email: referralDetails?.patient?.email || formData?.email,
        mobile_number: (formType === 'referral' && referralDetails?.patient?.mobile_number?.replace('+1', '')) || formData?.mobile_number?.replace('+1', ''),
        street: (formType === 'referral' && referralDetails?.patient?.address?.street) || formData?.street,
        city: (formType === 'referral' && referralDetails?.patient?.address?.city) || formData?.city,
        province: (formType === 'referral' && referralDetails?.patient?.address?.province)
          || formData?.province,
        postal_code: (formType === 'referral' && referralDetails?.patient?.address?.postal_code)
          || formData?.postal_code,
        gps_location: (formType === 'referral' && referralDetails?.patient?.address?.gps_location)
          || formData?.gps_location,
        type: formType,
        referral_id: referralDetails?.id ?? '',
        is_urgent: referralDetails?.is_urgent || isUrgent || false,
        should_notify: referralDetails?.patient?.should_notify ?? false,
      }));
    };

    initiate();

    // clear data when component unmounts
    // return () => {
    //   setFormData({});
    // };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ referralDetails, open, stepId ]);

  return (
    <div>
      <Accordion
        open={open}
        toggle={toggleAccordion}
        className={classNames('mb-3', {
          'sr-accordion-collapsed': open === stepId,
        })}
      >
        <AccordionItem>
          <AccordionHeader className="sr-accordion-header" targetId={stepId}>
            <Row className="align-items-center">
              <Col xs={3} md={1} style={{ width: '50px' }}>
                {
                  (saved || referralDetails?.patient?.id) && open !== stepId ? (<SavedIcon />) : (
                    <span
                      className={classNames('sr-accordion-number', {
                        'sr-accordion-number-collapsed': open === stepId,
                      })}
                    >
                      {stepId}
                    </span>
                  )
                }
              </Col>
              <Col xs={9} md={7} lg={8} xl={9}>
                <span className="sr-accordion-title">
                  {i18n.forms.patientInfo.title}
                </span>
              </Col>
              {!isCompleted && isUpdate && open === stepId && (
                <Col xs={12} md={4} lg={3} xl={2} className="sr-save-exit-container">
                  <CustomButton
                    id="btn-save-exit"
                    className="btn-lg sr-save-exit-btn"
                    color="primary"
                    onClick={(e: any) => onSaveExit(e)}
                    disabled={loading || loadingDetails || isSaving}
                    loading={isSaving}
                  >
                    {i18n.button.saveExit}
                  </CustomButton>
                </Col>
              )}
            </Row>
          </AccordionHeader>
          <AccordionBody accordionId={stepId}>
            <Row>
              <Col xs={1} style={{ width: '50px' }} />
              <Col xs={12} lg={11}>
                {/* PATIENT BASIC INFORMATION */}
                <div className="mb-4">
                  <Row className="mb-3">
                    <Col>
                      <span className="sr-form-title">{i18n.forms.patientInfo.basicInfo.title}</span>
                    </Col>
                  </Row>
                  {loadingDetails && (
                    <BasicInfoSkeleton />
                  )}
                  {!loadingDetails && (
                    <>
                      <Row className="mb-2">
                        <Col md={6}>
                          <InputField
                            id="send-ref-first-name"
                            name="first_name"
                            label={i18n.forms.patientInfo.basicInfo.firstName.label}
                            placeholder={i18n.forms.patientInfo.basicInfo.firstName.placeholder}
                            type="text"
                            required
                            defaultValue={formData.first_name}
                            value={formData.first_name}
                            fieldError={errors?.first_name ?? ''}
                            onInputChangeHandler={(value) => handleInputChange('first_name', value)}
                            isReadOnly={isCompleted}
                          />
                        </Col>
                        <Col md={6}>
                          <InputField
                            id="send-ref-last-name"
                            name="last_name"
                            label={i18n.forms.patientInfo.basicInfo.lastName.label}
                            placeholder={i18n.forms.patientInfo.basicInfo.lastName.placeholder}
                            type="text"
                            required
                            defaultValue={formData.last_name}
                            value={formData.last_name}
                            fieldError={errors?.last_name ?? ''}
                            onInputChangeHandler={(value) => handleInputChange('last_name', value)}
                            isReadOnly={isCompleted}
                          />
                        </Col>
                      </Row>
                      <Row className="mb-3">
                        <Col>
                          <InputField
                            id="send-ref-birthdate"
                            name="birthdate"
                            label={i18n.forms.patientInfo.basicInfo.birthday.label}
                            placeholder={i18n.forms.patientInfo.basicInfo.birthday.placeholder}
                            type="date"
                            required
                            defaultValue={formData.birthdate}
                            value={formData.birthdate}
                            fieldError={errors?.birthdate ?? ''}
                            onInputChangeHandler={(value) => handleInputChange('birthdate', value)}
                            isReadOnly={isCompleted}
                          />
                        </Col>
                      </Row>
                    </>
                  )}
                </div>
                {formType === 'referral' && (
                  <>
                    {/* PATIENT CONTACT INFORMATION */}
                    <div className="mb-4">
                      <Row className="mb-3">
                        <Col>
                          <span className="sr-form-title">{i18n.forms.patientInfo.contactInfo.title}</span>
                        </Col>
                      </Row>
                      {loadingDetails && (
                        <ContactInfoSkeleton />
                      )}
                      {!loadingDetails && (
                        <>
                          <Row className="mb-2">
                            <Col>
                              <InputField
                                id="send-ref-email"
                                name="email"
                                label={i18n.forms.patientInfo.contactInfo.email.label}
                                placeholder={i18n.forms.patientInfo.contactInfo.email.placeholder}
                                type="email"
                                defaultValue={formData.email}
                                value={formData.email}
                                fieldError={errors?.email ?? ''}
                                onInputChangeHandler={(value) => handleInputChange('email', value)}
                                isReadOnly={isCompleted}
                              />
                            </Col>
                          </Row>
                          <Row className="mb-3">
                            <Col>
                              {(!guest && formData?.should_notify && !errors?.mobile_number) && (
                                <div className="mb-3 sr-info-style">
                                  <InfoFilledIcon />
                                  <span className="sr-info-style-text">{i18n.mobileInfo}</span>
                                </div>
                              )}
                              <InputField
                                id="send-ref-mobile-number"
                                name="mobile_number"
                                label={i18n.forms.patientInfo.contactInfo.phone.label}
                                placeholder={i18n.forms.patientInfo.contactInfo.phone.placeholder}
                                type="text"
                                required
                                defaultValue={formData.mobile_number}
                                value={formData.mobile_number}
                                fieldError={errors?.mobile_number ?? ''}
                                onInputChangeHandler={(value) => handleInputChange('mobile_number', value)}
                                isReadOnly={isCompleted}
                                isMobile
                                mobileCountryCode="+1"
                                customClassName="custom-mobile-number-form-padding"
                              />
                              {/* Not offered to guests (ENH-052 plan D8). */}
                              {!loadingDetails && !guest && (
                                <span className="mt-3">
                                  <Checkbox
                                    id="should-notify-patient"
                                    name="should_notify"
                                    type="checkbox"
                                    formGrpClassName="should-notify-group"
                                    onInputChangeHandler={handleShouldNotify}
                                    label={(
                                      <span className="should-notify-label">{i18n.forms.patientInfo.shouldNotify}</span>
                                    )}
                                    checked={formData?.should_notify}
                                  />
                                </span>
                              )}
                            </Col>
                          </Row>
                        </>
                      )}
                    </div>
                    {/* PATIENT ADDRESS */}
                    <div className="mb-4">
                      <Row className="mb-3">
                        <Col>
                          <span className="sr-form-title">{i18n.forms.patientInfo.addressInfo.title}</span>
                        </Col>
                      </Row>
                      {loadingDetails && (
                        <AddressInfoSkeleton />
                      )}
                      {!loadingDetails && (
                        <>
                          <Row className="mb-2">
                            <Col>
                              <FormGroup floating className="form-group autocomplete-address-grp">
                                <Input
                                  id="input-street"
                                  name="street"
                                  placeholder={i18n.forms.patientInfo
                                    .addressInfo.street.placeholder}
                                  type="text"
                                  onFocus={handleOnFocus}
                                  onBlur={handleOnBlur}
                                  defaultValue={formData.street}
                                  invalid={!!errors?.street}
                                  value={formData.street}
                                  onChange={(evt) => {
                                    setFormData((prevState: any) => ({
                                      ...prevState,
                                      street: evt.target.value,
                                    }));
                                    getPlacePredictions({ input: evt.target.value });
                                  }}
                                  disabled={isCompleted}
                                />
                                {placePredictions.length > 0 && isFocused && (
                                  <ListGroup className="address-list-group">
                                    {placePredictions.map(prediction => (
                                      <ListGroupItem
                                        key={prediction.place_id}
                                        onClick={() => {
                                          onSelectAddress(prediction);
                                        }}
                                        style={{ cursor: 'pointer' }}
                                        action
                                      >
                                        {prediction.description}
                                      </ListGroupItem>
                                    ))}
                                  </ListGroup>
                                )}
                                <Label>
                                  {i18n.forms.patientInfo.addressInfo.street.label}
                                </Label>
                                <FormFeedback>
                                  {Array.isArray(errors?.street)
                                    ? errors.street.map((message: string, idx: number) => (
                                      <div key={`street-error-${idx.toString()}`}>{message}</div>
                                    ))
                                    : errors?.street}
                                </FormFeedback>
                              </FormGroup>
                            </Col>
                          </Row>
                          <Row className="mb-2">
                            <Col>
                              <InputField
                                id="send-ref-city"
                                name="city"
                                label={i18n.forms.patientInfo.addressInfo.city.label}
                                placeholder={i18n.forms.patientInfo.addressInfo.city.placeholder}
                                type="text"
                                defaultValue={formData.city}
                                value={formData.city}
                                fieldError={errors?.city ?? ''}
                                onInputChangeHandler={(value) => setFormData((prevState: any) => ({
                                  ...prevState,
                                  city: value,
                                }))}
                                isReadOnly={isCompleted}
                              />
                            </Col>
                          </Row>
                          <Row className="mb-3">
                            <Col md={6}>
                              <InputField
                                id="send-ref-state"
                                name="province"
                                label={i18n.forms.patientInfo.addressInfo.state.label}
                                placeholder={i18n.forms.patientInfo.addressInfo.state.placeholder}
                                type="text"
                                defaultValue={formData.province}
                                value={formData.province}
                                fieldError={errors?.province ?? ''}
                                onInputChangeHandler={(value) => setFormData((prevState: any) => ({
                                  ...prevState,
                                  province: value,
                                }))}
                                isReadOnly={isCompleted}
                              />
                            </Col>
                            <Col md={6}>
                              <InputField
                                id="send-ref-postal"
                                name="postal_code"
                                label={i18n.forms.patientInfo.addressInfo.postal.label}
                                placeholder={i18n.forms.patientInfo
                                  .addressInfo.postal.placeholder}
                                type="text"
                                defaultValue={formData.postal_code}
                                value={formData.postal_code}
                                fieldError={errors?.postal_code ?? ''}
                                onInputChangeHandler={(value) => setFormData((prevState: any) => ({
                                  ...prevState,
                                  postal_code: value,
                                }))}
                                isReadOnly={isCompleted}
                              />
                            </Col>
                          </Row>
                        </>
                      )}
                    </div>
                  </>
                )}
                {!isCompleted && (
                  <div className="mb-4">
                    <div className="mb-3 sr-info-style">
                      <InfoFilledIcon />
                      <span className="sr-info-style-text">{i18n.info}</span>
                    </div>
                    <div className="sr-footer-btn-container">
                      <CustomButton
                        id="btn-back"
                        className="btn-lg sr-back-btn"
                        color="secondary"
                        onClick={onHandleBack}
                        disabled={loading || loadingDetails}
                      >
                        {i18n.button.back}
                      </CustomButton>
                      <CustomButton
                        id="btn-next"
                        className="btn-lg sr-next-btn"
                        color="primary"
                        onClick={onHandleNext}
                        disabled={loading || loadingDetails}
                        loading={loading}
                      >
                        {i18n.button.next}
                      </CustomButton>
                    </div>
                  </div>
                )}
              </Col>
            </Row>
          </AccordionBody>
        </AccordionItem>
      </Accordion>
    </div>
  );
};

export default PatientInformationForm;
